import { isNoQrBank } from '@p2p/entities/qrInvoice/noQrBanks';
import {
  generateSberDeepLink,
  getDushanbeIosSchemaList,
  getDushanbeProviderPayLink,
  isDushanbeSberDeeplink,
} from '@p2p/pages/Redirect/deeplink/deeplink.utils';
import { TGetOrderResponse } from '@p2p/services';
import { hasUrlScheme } from '@p2p/shared/functions';
import { usePlatform } from '@p2p/shared/hooks';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import styles from './OpenBankAppButton.module.scss';
import { openIosSchemesInOrder } from './openIosSchemesInOrder';

type OpenBankAppButtonProps = {
  order: TGetOrderResponse;
};

/**
 * P2P-2511: кнопка «Открыть приложение банка» на платёжной странице main_page.
 *
 * Для SimplePay QR (SberPay) провайдер возвращает deeplink'и android_link /
 * ios_link (P2P-2509/2510). На телефоне сканировать собственный экран нельзя,
 * поэтому показываем прямую кнопку-deeplink под QR-кодом. На десктопе и при
 * отсутствии ссылки ничего не рендерим — остаётся обычный сценарий с QR.
 *
 * P2P-2618: изначально (P2P-2511) кнопка показывалась только для Сбера. Провайдер
 * подтвердил, что для ВТБ ссылка приходит в том же формате (deeplinks.android[] /
 * deeplinks.ios[] с code: "VTB", как и на бэке — см. P2P-2509/2510), поэтому
 * распространяем логику на sber и vtb. Примечания про Сбербанк под инструкцией
 * (InstructionInvoice) по-прежнему завязаны отдельно на client_bank_code = sber
 * и этим изменением не затрагиваются.
 *
 * P2P-2618 (доп.): для ВТБ с emvco (SberPay QR-флоу с emvco-пейлоадом заявки)
 * это не «просто открыть приложение», а полноценная оплата в один клик по
 * диплинку — текст и цвет кнопки меняются на синий (#3B4CE0), чтобы не путать
 * с оранжевой «Открыть приложение банка».
 *
 * P2P-2864: DC-Сбер (Душанбе Сити + sber, ссылка провайдера pay.dc.tj) на iOS.
 * У Сбербанк Онлайн на iPhone несколько сборок, у каждой своя схема, поэтому
 * одна прямая ссылка ios_link у части пользователей ничего не открывает.
 * По нажатию перебираем по очереди ровно ссылки провайдера (7 схем из его
 * deeplinks.ios, см. SBER_IOS_SCHEMA_LIST_DUSHANBE_PROVIDER) — так же, как его
 * чекаут: без ухода со страницы, см. openIosSchemesInOrder. Сначала пробовали
 * через redirect.html (перезагрузка страницы на каждую схему с шагом 50 мс) —
 * на iPhone Сбер не открывался. Android — по-прежнему прямой диплинк.
 *
 * P2P-2851: у MeridianPay (индонезийский QR) в ios_link / android_link лежит не
 * ссылка, а сам QR-пейлоад ("000201…"). С 03.11.2026 такие заявки открывают этот
 * экран, и при client_bank_code = sber кнопка вела бы в никуда. Значение без
 * URL-схемы ссылкой не считаем - остаётся QR.
 */
export const OpenBankAppButton = ({ order }: OpenBankAppButtonProps) => {
  const { isIOS, isAndroid } = usePlatform();

  const clientBankCode = order.client_bank_code?.toLowerCase();
  const isSupportedBank = clientBankCode === 'sber' || clientBankCode === 'vtb';
  const isVtbOneClick = clientBankCode === 'vtb' && Boolean(order.emvco);

  // iOS-ссылка приоритетна на iOS, android — на Android; иначе берём любую
  // доступную как запасной вариант.
  const link = isIOS
    ? order.ios_link || order.android_link
    : isAndroid
      ? order.android_link || order.ios_link
      : undefined;

  // Для банков без поддержки QR/диплинка (напр. Babilon-M) кнопку не показываем в любом случае.
  if (!isSupportedBank || !link || !hasUrlScheme(link) || isNoQrBank(order)) {
    return null;
  }

  const isDcSberIOS =
    isIOS && isDushanbeSberDeeplink(order) && Boolean(getDushanbeProviderPayLink(order));

  if (isDcSberIOS) {
    return <DcSberIosButton order={order} />;
  }

  return (
    <a
      className={classNames(styles.button, { [styles.buttonBlue]: isVtbOneClick })}
      href={link}
      rel="noopener noreferrer"
    >
      {isVtbOneClick ? 'Оплатить в один клик' : 'Открыть приложение банка'}
    </a>
  );
};

const DcSberIosButton = ({ order }: OpenBankAppButtonProps) => {
  const [isOpening, setIsOpening] = useState(false);
  const cancelRef = useRef<(() => void) | null>(null);

  useEffect(() => () => cancelRef.current?.(), []);

  const handleClick = () => {
    if (isOpening) return;

    const links = getDushanbeIosSchemaList(order).map((_, index) =>
      generateSberDeepLink(order, index),
    );

    setIsOpening(true);
    cancelRef.current = openIosSchemesInOrder(links, () => setIsOpening(false));
  };

  return (
    <button
      type="button"
      className={styles.button}
      onClick={handleClick}
      disabled={isOpening}
      aria-busy={isOpening}
    >
      {isOpening ? 'Открываем Сбер…' : 'Открыть приложение банка'}
    </button>
  );
};
